import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, Camera, ArrowsClockwise, Spinner, CheckCircle, Warning } from "@phosphor-icons/react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

/**
 * Full-screen live barcode scanner. Opens the device camera (preferring the
 * rear-facing one on phones), continuously decodes 1D/2D barcodes via zxing,
 * and on a successful read snaps the current video frame into a JPEG Blob
 * and hands it back to the parent for OCR. The image is never persisted —
 * the parent decides whether to upload it to /api/prescriptions/ocr.
 */
export default function LiveBarcodeScanner({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const readerRef = useRef(null);
  // Once we have a successful capture we lock the UI so the same frame isn't
  // processed multiple times by the continuous-decode callback.
  const lockedRef = useRef(false);

  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [status, setStatus] = useState("Starting camera…");
  const [error, setError] = useState("");

  const stopStream = useCallback(() => {
    try { controlsRef.current?.stop?.(); } catch { /* ignore */ }
    controlsRef.current = null;
    const v = videoRef.current;
    if (v?.srcObject) {
      try { v.srcObject.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      v.srcObject = null;
    }
  }, []);

  const startReader = useCallback(async (preferredId) => {
    if (!videoRef.current) return;
    stopStream();
    lockedRef.current = false;
    setStatus("Point the camera at the barcode…");
    setError("");
    try {
      const reader = readerRef.current;
      controlsRef.current = await reader.decodeFromVideoDevice(
        preferredId || undefined,
        videoRef.current,
        (result, err, controls) => {
          if (lockedRef.current) return;
          if (result) {
            lockedRef.current = true;
            try { controls.stop(); } catch { /* ignore */ }
            let fmtName = null;
            try {
              const fmt = result.getBarcodeFormat?.();
              if (fmt != null) {
                fmtName = Object.keys(BarcodeFormat).find((k) => BarcodeFormat[k] === fmt) || null;
              }
            } catch { /* format is optional */ }
            handleDecoded(result.getText(), fmtName);
          }
          // zxing fires `err` on every frame without a barcode — ignore.
        }
      );
    } catch (e) {
      setError(e?.message || "Could not start the camera. Check browser permissions.");
      setStatus("");
    }
  }, [stopStream]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDecoded = useCallback(async (barcodeText, barcodeFormat) => {
    setStatus("Got it — capturing frame…");
    const v = videoRef.current;
    if (!v || v.videoWidth === 0) {
      onCapture({ barcode: barcodeText, barcodeFormat, frameBlob: null });
      return;
    }
    // Snap the current video frame to a JPEG blob so the parent can run OCR
    // on the very frame that yielded the barcode.
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
    stopStream();
    onCapture({ barcode: barcodeText, barcodeFormat, frameBlob: blob });
  }, [onCapture, stopStream]);

  // Initial setup: build the reader, enumerate cameras, pick the rear one.
  useEffect(() => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.QR_CODE, BarcodeFormat.EAN_13, BarcodeFormat.UPC_A,
      BarcodeFormat.PDF_417,
    ]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    readerRef.current = new BrowserMultiFormatReader(hints);

    let cancelled = false;
    (async () => {
      try {
        // Ask for camera permission first so device labels populate.
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
        stream.getTracks().forEach((t) => t.stop());
        const list = (await BrowserMultiFormatReader.listVideoInputDevices()) || [];
        if (cancelled) return;
        setDevices(list);
        // Prefer a back-facing camera by label heuristics; otherwise first.
        const rear = list.find((d) => /back|rear|environment/i.test(d.label)) || list[0];
        const id = rear?.deviceId || "";
        setDeviceId(id);
        await startReader(id);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Camera permission denied.");
          setStatus("");
        }
      }
    })();
    return () => { cancelled = true; stopStream(); };
  }, [startReader, stopStream]);

  function switchCamera() {
    if (devices.length < 2) return;
    const idx = devices.findIndex((d) => d.deviceId === deviceId);
    const next = devices[(idx + 1) % devices.length];
    setDeviceId(next.deviceId);
    startReader(next.deviceId);
  }

  return (
    <div
      className="modal-backdrop"
      data-testid="live-scan-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) { stopStream(); onClose(); } }}
      style={{ zIndex: 200 }}
    >
      <div
        className="modal-panel"
        style={{
          maxWidth: 560,
          width: "100%",
          height: "auto",
          background: "#0F172A",
          color: "#F8FAFC",
          border: "1px solid #1E293B",
          padding: 0,
          overflow: "hidden",
        }}
        data-testid="live-scan-modal"
      >
        <div
          className="modal-header"
          style={{
            background: "transparent",
            color: "#F8FAFC",
            borderBottom: "1px solid #1E293B",
            display: "flex", alignItems: "center", gap: 8,
            padding: "10px 14px",
          }}
        >
          <Camera size={16} weight="bold" />
          <span style={{ fontWeight: 700, letterSpacing: "0.06em" }}>LIVE BARCODE SCAN</span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            {devices.length > 1 && (
              <button
                type="button"
                onClick={switchCamera}
                className="btn btn--outline btn--sm"
                title="Switch camera"
                data-testid="live-scan-switch-camera"
                style={{ color: "#F8FAFC", borderColor: "#475569", background: "transparent" }}
              >
                <ArrowsClockwise size={12} weight="bold" />
              </button>
            )}
            <button
              type="button"
              onClick={() => { stopStream(); onClose(); }}
              data-testid="live-scan-close"
              aria-label="Close live scanner"
              style={{
                background: "transparent", border: "none", color: "#F8FAFC",
                cursor: "pointer", padding: 4,
              }}
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>

        <div
          style={{
            position: "relative",
            background: "#000",
            aspectRatio: "4 / 3",
            width: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <video
            ref={videoRef}
            data-testid="live-scan-video"
            playsInline
            muted
            autoPlay
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          {/* Reticle */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute", left: "10%", right: "10%", top: "30%", bottom: "30%",
              border: "2px solid rgba(250, 204, 21, 0.85)",
              borderRadius: 6,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
              pointerEvents: "none",
            }}
          />
          {/* Sweep line */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute", left: "12%", right: "12%", top: "50%",
              height: 2, background: "rgba(250, 204, 21, 0.9)",
              boxShadow: "0 0 12px rgba(250, 204, 21, 0.9)",
              animation: "live-scan-sweep 1.8s ease-in-out infinite",
              pointerEvents: "none",
            }}
          />
          <style>{`@keyframes live-scan-sweep { 0%{transform:translateY(-60px)} 50%{transform:translateY(60px)} 100%{transform:translateY(-60px)} }`}</style>
        </div>

        <div
          style={{
            padding: "10px 14px",
            background: "#0F172A",
            borderTop: "1px solid #1E293B",
            display: "flex", alignItems: "center", gap: 8, fontSize: 12,
          }}
          data-testid="live-scan-status"
        >
          {error ? (
            <>
              <Warning size={14} weight="bold" color="#F87171" />
              <span style={{ color: "#FECACA" }}>{error}</span>
            </>
          ) : (
            <>
              {lockedRef.current
                ? <CheckCircle size={14} weight="fill" color="#34D399" />
                : <Spinner size={14} weight="bold" className="spin" />}
              <span style={{ color: "#CBD5E1" }}>{status || "Searching for a barcode…"}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
